/**
 * English translations for LinCal
 */
export default {
  // Common buttons and labels
  common: {
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    create: 'Create',
    edit: 'Edit',
    add: 'Add',
    ok: 'OK',
    confirm: 'Confirm',
    loading: 'Loading...',
  },

  // App title
  app: {
    title: 'LinCal',
  },

  // Authentication
  auth: {
    login: 'Log In',
    logout: 'Log Out',
    loginTooltip: 'Log in to sync your calendar across devices',
    logoutTooltip: 'Log out of your account',
    loginErrorTitle: 'Login Failed',
    loginErrorMessage: 'Authentication could not be completed. Please try again. Reason: {reason}',
    serverOffline: 'The authentication server is currently unavailable. Please check your connection and try again later.',
  },

  // Sync status
  sync: {
    connected: 'Connected - changes sync automatically',
    connecting: 'Connecting...',
    offline: 'Offline mode - using cached data (read-only)',
    error: 'Connection error - no cached data available',
  },

  // Sharing
  sharing: {
    title: 'Sharing',
    myShares: 'My Shares',
    emailPlaceholder: 'Enter email address',
    searching: 'Searching...',
    canView: 'Can view',
    canEdit: 'Can edit',
    share: 'Share',
    recentContacts: 'Recent Contacts',
    sharedWith: 'Shared with',
    notShared: 'No projects are shared yet.',
    removeAccess: 'Remove Access',
    removeAccessConfirm: 'Remove access for {name}? They will no longer be able to view or edit this project.',
    shareButton: 'Share',
    addNew: 'Add new share',
    selectProjects: 'Select projects to share',
    selectAll: 'Select all',
    noProjects: 'No projects available to share.',
    selectProjectsError: 'Please select at least one project to share.',
    invalidEmail: 'Please enter a valid email address.',
    alreadyShared: '{projects} is already shared with {email}.',
    sharedWithMe: 'Shared with Me',
    noSharedProjects: 'No projects have been shared with you yet.',
    owner: 'Owner',
    includeInCalendar: 'Include in Calendar',
    removeFromCalendar: 'Remove from Calendar',
    includedInCalendar: 'Included in your calendar',
    sharedProject: 'Shared project',
    sharedBy: 'Shared by',
    sharedByOn: 'Shared by {name} on {date}',
    originalName: 'Original name: {name}',
    readOnlyInfo: 'This item is read-only because it belongs to a shared project.',
  },

  // iCal publishing
  ical: {
    title: 'iCal Feed Settings',
    description: 'Enable iCal publishing to subscribe to your calendar from other apps like Google Calendar, Apple Calendar, or Outlook.',
    enableFeed: 'Enable iCal feed',
    feedUrl: 'Feed URL',
    copy: 'Copy',
    copied: 'Copied!',
    urlHint: 'Use this URL to subscribe from other calendar applications.',
    regenerateToken: 'Regenerate URL',
    regenerateWarning: 'Warning: Regenerating the URL will break existing subscriptions.',
    settingsButton: 'iCal Settings',
  },

  // Data merge (first login flow)
  dataMerge: {
    title: 'Local Data Found',
    description: 'You have existing calendar data stored locally in your browser. What would you like to do with it?',
    localData: 'Your local data includes:',
    projects: '{count} project(s)',
    events: '{count} event(s)',
    milestones: '{count} milestone(s)',
    mergeOption: 'Merge with Cloud',
    mergeDescription: 'Upload your local data to your cloud calendar. All items will be synced.',
    discardOption: 'Discard Local Data',
    discardDescription: 'Start fresh with your cloud calendar. Local data will be permanently deleted.',
    processing: 'Processing...',
  },

  // Calendar navigation, structure, and management
  calendar: {
    // Navigation
    months: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    monthsShort: [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ],
    daysShort: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    previousYear: 'Previous year',
    nextYear: 'Next year',
    goToCurrentYear: 'Go to current year',
    // Multi-calendar management
    selectCalendar: 'Select Calendar',
    myCalendars: 'My Calendars',
    sharedWithMe: 'Shared with Me',
    default: 'Default',
    canEdit: 'Can edit',
    viewOnly: 'View only',
    manageCalendars: 'Manage Calendars',
    createCalendar: 'Create Calendar',
    deleteCalendar: 'Delete Calendar',
    deleteCalendarConfirm: 'Are you sure you want to delete "{name}"? This will permanently delete all events and milestones in this calendar.',
    name: 'Name',
    namePlaceholder: 'My Calendar',
    nameRequired: 'Calendar name is required',
    color: 'Color',
  },

  // Settings modal
  settings: {
    title: 'Settings',
    language: 'Language',
    manageHolidays: 'Manage Holidays',
    data: 'Data',
    exportJSON: 'Export JSON Backup',
    exportICS: 'Export iCalendar...',
    importJSON: 'Import JSON Backup',
    languages: {
      en: 'English',
      de: 'Deutsch',
    },
  },

  // Header
  header: {
    addProject: 'Add project',
    editProject: 'Edit project',
    showProject: 'Show project',
    hideProject: 'Hide project',
    projectSelected: '{name} - Click to select (selected)',
    projectNotSelected: '{name} - Click to select',
    about: 'About LinCal',
    settings: 'Settings',
  },

  // Events
  event: {
    new: 'New Event',
    edit: 'Edit Event',
    title: 'Title',
    description: 'Description',
    placeholder: 'Event name...',
    descriptionPlaceholder: 'Add a description...',
    startDate: 'Start Date',
    endDate: 'End Date',
    project: 'Project',
    lockStart: 'Lock start date',
    unlockStart: 'Unlock start date',
    lockEnd: 'Lock end date',
    unlockEnd: 'Unlock end date',
    lockDuration: 'Lock duration',
    unlockDuration: 'Unlock duration',
    duration: '{days} day ({workdays} workday)',
    durationPlural: '{days} days ({workdays} workdays)',
    durationSingularWorkdays: '{days} day ({workdays} workdays)',
    durationPluralWorkday: '{days} days ({workdays} workday)',
  },

  // Milestones
  milestone: {
    new: 'New Milestone',
    edit: 'Edit Milestone',
    placeholder: 'Milestone name...',
    date: 'Date',
    icon: 'Icon',
    lockDate: 'Lock date',
    unlockDate: 'Unlock date',
    icons: {
      flag: 'Flag',
      diamond: 'Diamond',
      star: 'Star',
      circle: 'Circle',
    },
  },

  // Holidays
  holiday: {
    title: 'Manage Holidays',
    empty: 'No holidays yet. Add one below!',
    add: '+ Add Holiday',
    edit: 'Edit holiday',
    delete: 'Delete holiday',
    placeholder: 'Holiday name...',
    multiDay: 'Multi-day',
    startDate: 'Start Date',
    endDate: 'End Date',
    date: 'Date',
  },

  // Projects
  project: {
    new: 'New Project',
    edit: 'Edit Project',
    name: 'Name',
    placeholder: 'Project name...',
    color: 'Color',
    select: 'Select project...',
    defaultName: 'Personal',
    holidaysName: 'Holidays',
    cannotDeleteLast: 'Cannot delete the last project. Add a new project first.',
    moveItemsTo: 'Move events and milestones to',
    deleteItems: '(Delete all events and milestones)',
    confirmDelete: 'Confirm Delete',
  },

  // Context menu
  contextMenu: {
    addEvent: 'Add Event',
    addMilestone: 'Add Milestone',
  },

  // Dialogs
  dialog: {
    deleteEvent: 'Delete Event',
    deleteMilestone: 'Delete Milestone',
    deleteProject: 'Delete Project',
    deleteHoliday: 'Delete Holiday',
    deleteEventConfirm: 'Delete event "{title}"?',
    deleteMilestoneConfirm: 'Delete milestone "{title}"?',
    deleteProjectConfirmMerge: 'Delete project "{name}" and move its events/milestones to "{targetName}"?',
    deleteProjectConfirmDelete: 'Delete project "{name}" and all its events/milestones? This cannot be undone.',
    deleteProjectLastWarning: 'Cannot delete the last project. At least one project must remain. Please add a new project first.',
    deleteHolidayConfirm: 'Delete this holiday?',
    importTitle: 'Import Data',
    importConfirm: 'Import this data? This will replace all existing data.',
    importSuccess: 'Success',
    importSuccessMessage: 'Data imported successfully!',
    importFailed: 'Import Failed',
    importFailedMessage: 'Failed to import data: {error}',
    exportFailed: 'Export Failed',
    exportFailedMessage: 'Failed to export data: {error}',
  },

  // Announcement modal
  announcement: {
    welcomeTitle: 'Welcome to LinCal',
    changelogTitle: "What's New in LinCal",
    aboutTitle: 'About LinCal',
    getStarted: 'Get Started',
    gotIt: 'Got it',
    whatsNew: "What's New",
    recentChanges: 'Recent Changes',
    version: 'Version {version}',
    welcomeIntro: "LinCal is a linear yearly calendar that shows your entire year at a glance. Create events by dragging across days, add milestones for important dates, and organize everything with colored projects.",
    storageWarning: "Your data is stored locally in your browser. Use the Export button regularly to back up your calendar, especially before clearing browser data or switching devices.",
    storageServer: "Your data is stored securely on our server and syncs automatically across all your devices.",
    chooseHowToUse: 'Choose how to use LinCal',
    loginOption: 'Log In',
    loginOptionRecommended: 'Log In (Recommended)',
    loginOptionDesc: 'Sync across devices, protected by your account.',
    localOption: 'Use Locally Only',
    localOptionDesc: 'Data stored in this browser only.',
    localWarningShort: 'Local data can be lost if you clear browser data or switch devices. You can log in later to sync.',
    important: 'Important:',
    note: 'Note:',
  },

  // Error fallback
  error: {
    title: 'Something went wrong',
    message: 'The calendar encountered an unexpected error.',
    technicalDetails: 'Technical details',
    tryAgain: 'Try Again',
    reloadPage: 'Reload Page',
    resetData: 'Reset Data',
    resetConfirm: 'This will delete all your calendar data and cannot be undone. Continue?',
    storageFull: 'Failed to save data. LocalStorage might be full.',
    exportFailed: 'Failed to export data',
    invalidFormat: 'Invalid file format: missing required fields',
    invalidArrays: 'Invalid file format: fields must be arrays',
    parseFailed: 'Failed to parse JSON file: {error}',
    readFailed: 'Failed to read file',
    invalidData: 'Invalid data structure, using defaults',
  },

  // Year progress
  yearProgress: {
    tooltip: 'Day {day} of {daysInYear}\n{percent}% complete - {remaining} days remaining',
    ariaLabel: '{percent}% of {year} complete',
    messages: {
      // 0-5%: Early January - Fresh Start
      early: [
        "{year} just started - a whole year of possibilities!",
        "Fresh year, fresh goals. Let's go!",
        "Day {day} of {daysInYear}. The adventure begins.",
        "New year energy: 100%. Let's make it count.",
        "{year} is a blank canvas. Start painting!",
        "Plot twist: you survived another year. Now what?",
        "The year is so new it still has that new-year smell."
      ],

      // 5-15%: January/February - Building Momentum
      building: [
        "{percent}% in. Still riding that new year energy?",
        "Day {day} of {daysInYear}. Habits are forming.",
        "{percent}% of {year} done. How are those resolutions?",
        "Early days. Big potential. {percent}% complete.",
        "{percent}% through {year}. The momentum is building!",
        "Week {week} vibes. Keep the energy high!"
      ],

      // 15-25%: Q1 - Getting Serious
      q1: [
        "{percent}% through {year}. Time flies when you're productive!",
        "Q1 vibes: {percent}% done, still on track?",
        "{percent}% of the year used. Spend the rest wisely.",
        "Day {day}. That's {percent}% of your year. Make it count!",
        "First quarter energy: {percent}% in the books.",
        "{percent}% complete. The year is picking up speed!"
      ],

      // 25-40%: Spring - Quarter Mark
      spring: [
        "Quarter of {year} is history. What's your story so far?",
        "{percent}% gone. Still {remaining}% to make magic happen.",
        "Spring check-in: {percent}% of {year} complete.",
        "{percent}% through. Are you where you wanted to be?",
        "One quarter down! {percent}% of {year} in the books.",
        "{percent}% complete. Spring has sprung, so has your progress!"
      ],

      // 40-50%: Approaching Midpoint
      approaching: [
        "Almost halfway! {percent}% of {year} in the books.",
        "The midpoint approaches... {percent}% complete.",
        "{percent}% done. Time for a halftime strategy review?",
        "Counting down to halftime: {percent}% through {year}.",
        "{percent}% of {year} complete. The halfway mark is near!"
      ],

      // 50%: Halftime - Special milestone
      halftime: [
        "HALFTIME! 50% of {year} complete. How's your score?",
        "Halfway through {year}. Plot twist or staying the course?",
        "50/50. Half spent, half to invest. Choose wisely.",
        "The year is perfectly split. Time to reflect and refocus.",
        "We're at the equator of {year}. Careful not to fall off.",
        "Half the year gone. Half the snacks eaten. Coincidence?"
      ],

      // 50-65%: Post-Halftime
      postHalf: [
        "Second half of {year} is underway. {percent}% total.",
        "More behind than ahead now. {percent}% complete.",
        "{percent}% through. The home stretch starts here.",
        "Past the midpoint at {percent}%. Keep the momentum!",
        "{percent}% of {year} done. Second half, let's go!"
      ],

      // 65-75%: Q3 - Three Quarter Push
      q3: [
        "Two-thirds of {year} done. Sprint or marathon from here?",
        "{percent}% complete. Q4 is on the horizon.",
        "Day {day} of {daysInYear}. Only {remaining}% of {year} remains!",
        "{percent}% through. Time to finish strong!",
        "Q3 check-in: {percent}% of {year} in the books."
      ],

      // 75-85%: Q4 Begins
      q4: [
        "Three quarters down! {percent}% of {year} complete.",
        "Q4 energy: {remaining}% left to crush your goals.",
        "{percent}% done. The final quarter is here!",
        "Home stretch! Only {remaining}% of {year} to go.",
        "{percent}% of {year} complete. Final quarter, here we go!"
      ],

      // 85-95%: Year-End Push
      yearEnd: [
        "Crunch time! {percent}% of {year} is gone.",
        "Only {remaining}% left. Make every day count!",
        "{percent}% complete. Wrapping up {year} strong?",
        "The finish line is in sight! {percent}% done.",
        "{percent}% of {year} in the books. Final sprint mode!"
      ],

      // 95-99%: Final Days
      final: [
        "{year} is almost a wrap! {percent}% complete.",
        "Final countdown: only {remaining}% of {year} remains.",
        "Last {daysRemaining} days of {year}. Any unfinished business?",
        "{year}'s closing credits are rolling... {percent}% done.",
        "Sprint finish! {daysRemaining} days to wrap up the year.",
        "The year-end boss battle begins. {daysRemaining} days left."
      ],

      // 99-100%: Last Day(s)
      lastDays: [
        "Final day(s) of {year}! What a year it's been.",
        "{year}: {percent}% complete. See you next year!",
        "The last page of {year}. Time to start a new chapter.",
        "Wrapping up {year}. Ready for the next adventure?",
        "{year} is basically done. Time to pretend you achieved everything."
      ]
    }
  },

  // iCalendar export modal
  icalExport: {
    title: 'Export to iCalendar',
    selectProjects: 'Select projects to include:',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    export: 'Export',
    hiddenSuffix: '(hidden)',
  },

  // Empty state
  emptyState: {
    title: 'Welcome to Your Calendar!',
    createEventTitle: 'Create an Event',
    createEventDesc: 'Drag across days in the calendar grid to create an event',
    addMilestoneTitle: 'Add a Milestone',
    addMilestoneDesc: 'Right-click on any day and select "Add Milestone"',
    createProjectTitle: 'Create Projects',
    createProjectDesc: 'Click the + button in the header to organize your events with colored projects',
    helpTitle: 'Need Help?',
    helpDesc: 'Press ? to see all keyboard shortcuts and interactions',
  },

  // Help modal
  help: {
    title: 'Keyboard Shortcuts & Help',
    keyboardShortcuts: 'Keyboard Shortcuts',
    interactions: 'Interactions',
    // Keyboard shortcuts
    escapeKey: 'Close sidebar or modal',
    zoomKey: 'Zoom in/out',
    panKey: 'Pan calendar view',
    contextMenuKey: 'Open context menu',
    helpKey: 'Show this help',
    // Keyboard labels
    scroll: 'Scroll',
    middleClick: 'Middle Click',
    rightClick: 'Right Click',
    drag: 'Drag',
    // Interactions
    createEvent: 'Create Event',
    createEventDesc: 'Drag across days in the calendar grid',
    editEvent: 'Edit Event/Milestone',
    editEventDesc: 'Click on an event bar or milestone marker',
    resizeEvent: 'Resize Event',
    resizeEventDesc: 'Drag the left or right edge of an event bar',
    moveEvent: 'Move Event',
    moveEventDesc: 'Drag an event bar to a new position',
    projectPills: 'Project Pills',
    projectPillsDesc: 'Click to select, drag to reorder, right-click to edit',
  },

  // Changelog entries
  changelog: {
    v090: {
      title: 'Modern UI & Smart Zoom',
      highlights: [
        'Redesigned interface with a modern, polished look',
        'Year progress indicator shows how much of the year has passed',
        'Smart zoom automatically fills viewport width on load and resize',
        'Unified tooltip styling across all elements',
        'Fixed multi-month event drag behavior',
        'Improved project pill interactions in header',
      ],
    },
    v080: {
      title: 'Initial Public Release',
      highlights: [
        'Full year calendar view with zoom and pan',
        'Create events by dragging across days',
        'Milestones with customizable icons',
        'Organize with colored projects',
        'Holiday management for non-working days',
        'Import/export your data as JSON backup',
      ],
    },
  },
};
